"use strict";

/**
 * Reload-survival for the cockpit (owner live-use defect: reloading the
 * browser always went back to Today and refetched everything). Two
 * independent mechanisms, both best-effort and wrapped in try/catch —
 * neither is ever a requirement for the app to work, only a convenience:
 *
 *  - The page address (hash) carries the active tab, the Plan week and the
 *    selected project codes, so a reload (or a shared/bookmarked link)
 *    restores the exact view instead of always landing on Today. This is
 *    ADDITIVE to projectFilterStorage.js's own localStorage persistence of
 *    the project selection, which keeps working unchanged and is still the
 *    fallback when the hash carries no `projects` (fresh tab, no history).
 *  - sessionStorage mirrors the last successfully fetched envelope for
 *    Today/Plan(per week)/NCRs/Reports so a reload can render that data
 *    immediately, before the fresh network read (always still triggered)
 *    lands.
 */

const HASH_SCREEN_KEYS = ["today", "plan", "ncrs", "weekly"];

/**
 * A `week=` value from the hash to the Monday of its ISO week, or `null`
 * for anything that is not a real calendar date (codex review finding,
 * PR #1957: a restored non-Monday or impossible date — e.g. a hand-edited
 * `2026-10-08` (a Thursday) or `2026-02-30` — passed the old regex-only
 * check, the API then rejected it, and Retry just resent the same bad
 * date). `Date.UTC` silently rolls an impossible day-of-month over to the
 * next month, so a plain `Date.parse` round-trip is not enough — this
 * re-checks the constructed date's own y/m/d against the input digits.
 * A valid-but-non-Monday date is normalised to its Monday rather than
 * rejected, same rule as App.jsx's own `mondayOf`.
 */
export function normalizeWeekStart(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  const dow = date.getUTCDay();
  const diff = (dow + 6) % 7; // 0 for Monday
  date.setUTCDate(date.getUTCDate() - diff);
  return date.toISOString().slice(0, 10);
}

/**
 * Parses `#screen=plan&week=2026-10-06&projects=SS418,SS412413` →
 * `{ screen, weekStart, selectedCodes }`, any/all fields optional. An
 * invalid `week` is dropped (never included in the result) rather than
 * passed through — the caller's own fallback (`mondayOf()`) applies,
 * exactly as if the hash carried no week at all.
 */
export function readPageHash() {
  try {
    const raw = String(globalThis.location.hash || "").replace(/^#/, "");
    if (!raw) return {};
    const params = new URLSearchParams(raw);
    const result = {};
    const screen = params.get("screen");
    if (screen && HASH_SCREEN_KEYS.includes(screen)) result.screen = screen;
    const week = params.get("week");
    if (week) {
      const normalized = normalizeWeekStart(week);
      if (normalized) result.weekStart = normalized;
    }
    const projects = params.get("projects");
    if (projects !== null) {
      result.selectedCodes = projects ? projects.split(",").filter(Boolean) : [];
    }
    return result;
  } catch {
    return {};
  }
}

/** Best-effort `history.replaceState` — never pushes a new history entry, never throws. */
export function writePageHash({ screen, weekStart, selectedCodes }) {
  try {
    const params = new URLSearchParams();
    if (screen) params.set("screen", screen);
    if (weekStart) params.set("week", weekStart);
    if (selectedCodes) params.set("projects", selectedCodes.join(","));
    const next = `#${params.toString()}`;
    if (next === globalThis.location.hash) return;
    globalThis.history.replaceState(null, "", next);
  } catch {
    // No history API (SSR/test harness) or a blocked call — the hash is a
    // convenience, never required for the screen to render.
  }
}

const SESSION_PREFIX = "sm-cockpit:lastFetch:";

/** The last successfully cached value for `key`, or `null` on a miss or any storage failure. */
export function loadSessionCache(key) {
  try {
    const raw = globalThis.sessionStorage.getItem(`${SESSION_PREFIX}${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Best-effort persist; a storage failure (private window, quota, blocked) is swallowed, never thrown. */
export function saveSessionCache(key, value) {
  try {
    globalThis.sessionStorage.setItem(`${SESSION_PREFIX}${key}`, JSON.stringify(value));
  } catch {
    // Storage unavailable — this cache is a render-immediately convenience,
    // never a requirement; the live fetch still runs regardless.
  }
}

/**
 * Drops a cached snapshot (codex review finding, PR #1957 HIGH: a write's
 * read-back must never silently keep showing pre-write data — the
 * sessionStorage snapshot has to be invalidated right alongside the
 * in-memory cache, or a reload right after a failed post-write refresh
 * would resurrect the stale snapshot). Never throws.
 */
export function removeSessionCache(key) {
  try {
    globalThis.sessionStorage.removeItem(`${SESSION_PREFIX}${key}`);
  } catch {
    // Storage unavailable — nothing to drop either way.
  }
}

/**
 * Decides how App.jsx's `loadPlan` should surface a failed fetch, given
 * whether the call was a post-write read-back and whether a cache entry
 * existed going in (codex review finding, PR #1957, HIGH). `isWrite`
 * always forces `"error"` — a write's own read-back must never be
 * silently absorbed by a cache entry (App.jsx deletes that entry before
 * the fetch, so `hadCache` is normally already false for a write call;
 * this still forces `"error"` even if that invalidation were ever
 * skipped, which is the exact bug this finding reported: a cache hit
 * suppressed the error branch regardless of why the call was made). A
 * plain nav miss with nothing to fall back on is the same: `"error"`.
 * Only a plain nav CACHE HIT whose background revalidation failed is
 * `"stale"` — the cached data stays on screen, marked, never silently
 * treated as reconfirmed.
 */
export function planLoadFailureOutcome({ isWrite = false, hadCache = false } = {}) {
  return isWrite || !hadCache ? "error" : "stale";
}

/**
 * Per-week commit-ordering gate for App.jsx's Plan cache (live-journey
 * release-blocker regression, runs 37597768032/37597701646: a drag's write
 * read-back landed correctly, but a background prefetch for the SAME week
 * — started earlier, before the drag, while viewing the prior week, and
 * slow enough to still be in flight — resolved AFTER the write and
 * silently overwrote the cache AND the sessionStorage snapshot with its
 * own stale, pre-write data. Neither `loadPlan`'s generation counter
 * (write-only, guards the DISPLAYED envelope against a superseded
 * navigation) nor `isWrite`'s cache deletion (only protects what existed
 * BEFORE the write's own fetch starts) guarded against a slower, OLDER
 * request's result committing AFTER a newer one — so a reload right after
 * the drag re-seeded its initial render from exactly that clobbered
 * sessionStorage snapshot, and the un-moved tile never visibly corrected
 * itself.
 *
 * `start()` stamps a call with a monotonically increasing number at CALL
 * TIME (before its fetch is even issued) — call order, never resolution
 * order. `tryCommit(weekStart, stamp)` is checked right before writing a
 * result into the cache/sessionStorage for that week: a call may commit
 * only if no LATER-STARTED call for that same week has already committed,
 * so an earlier (and therefore staler) request can never clobber a newer
 * one's result no matter how long its own round trip takes.
 *
 * `startWrite(weekStart)` is `start()` for a write (drag/import) call,
 * additionally recording an EPOCH for that week AT CALL TIME — codex
 * review finding, PR #1963 P2b: a plain "only the latest COMMIT wins"
 * rule (checking only against `lastCommitted`) still has a gap, because
 * `lastCommitted` is only updated when a call actually RESOLVES. A
 * prefetch that started before a write, but resolves BEFORE the write
 * itself does, would see nothing committed yet and would be allowed to
 * commit its own stale data into the very cache entry the write had just
 * invalidated — a real window, not a hypothetical one, since `isWrite`
 * deletes the cache synchronously at call time but its own fetch can take
 * just as long as anyone else's. Recording the write's epoch at its own
 * CALL time (not its resolution) closes this: `tryCommit` refuses any
 * stamp — committed before, during, or after the write resolves — that
 * is older than the latest write's epoch for that week.
 */
export function createPlanCommitGate() {
  const lastCommitted = new Map(); // weekStart -> highest stamp committed so far
  const writeEpoch = new Map(); // weekStart -> stamp of the most recently STARTED write for that week
  let nextStamp = 0;
  function start() {
    nextStamp += 1;
    return nextStamp;
  }
  return {
    start,
    startWrite(weekStart) {
      const stamp = start();
      writeEpoch.set(weekStart, stamp);
      return stamp;
    },
    /** True when a newer call for this week has already committed (or a newer write started): this call's FAILURE must not be shown. */
    isStale(weekStart, stamp) {
      return stamp < Math.max(lastCommitted.get(weekStart) || 0, writeEpoch.get(weekStart) || 0);
    },
    tryCommit(weekStart, stamp) {
      const floor = Math.max(lastCommitted.get(weekStart) || 0, writeEpoch.get(weekStart) || 0);
      if (stamp < floor) return false;
      lastCommitted.set(weekStart, stamp);
      return true;
    },
  };
}

/**
 * What App.jsx#loadToday does with one answered read of GET /api/cockpit/today.
 * Returns { action: "apply", body } | { action: "ignore" } | { action: "notice" }
 * | { action: "error", errorCode }.
 * - A 200 is only an envelope when its body is an object with a `projects`
 *   array; anything else (an unparseable body arrives as null) is a failure,
 *   never stored (a null envelope renders as endless "Loading...").
 * - A read superseded by a newer one is dropped, unless nothing has landed yet.
 * - A failed refresh with a screen showing only warns (`notice`); with no
 *   envelope to keep, every failure is an `error` (Retry), quiet or not.
 */
export function decideTodayLoad({ result, quiet, superseded, hasEnvelope, gen = 0, lastAppliedGen = 0 }) {
  // An older read can never overwrite a newer one that was already applied (the
  // applied flag is set synchronously, before React re-renders).
  if (gen && gen < lastAppliedGen) return { action: "ignore" };
  const valid = Boolean(result && result.ok && result.body && typeof result.body === "object" && Array.isArray(result.body.projects));
  if (superseded && (hasEnvelope || !valid)) return { action: "ignore" };
  if (valid) return { action: "apply", body: result.body };
  const errorCode = result && result.ok ? "bad_response" : (result && result.body && result.body.errorCode) || `http_${(result && result.status) || 0}`;
  if (quiet && hasEnvelope) return { action: "notice" };
  return { action: "error", errorCode };
}

/**
 * Whether Today should re-read because the day may have turned over. The
 * payload carries no time zone, so the project-local day is the SERVER's: the
 * screen re-reads (and the server's `day.date` then wins) every 10 minutes
 * while the tab is visible, and at once when the shown day is two or more UTC
 * dates behind now (no zone is more than 14 hours from UTC, so that day is
 * over everywhere). A hidden tab waits for focus/visibility.
 */
export function todayRefreshDue({ shownDate, lastReadAtMs, nowMs, visible }) {
  if (!shownDate || !visible) return false;
  const shown = Date.parse(`${shownDate}T00:00:00Z`);
  if (!Number.isFinite(shown)) return false;
  const nowDay = Date.parse(`${new Date(nowMs).toISOString().slice(0, 10)}T00:00:00Z`);
  if (nowDay - shown >= 2 * 86400000) return nowMs - lastReadAtMs >= 60000; // at most one attempt a minute
  return nowMs - lastReadAtMs >= 10 * 60000;
}

/** The notice when a quiet Today refresh fails: after a write the data WAS saved; a background refresh saved nothing. */
export function todayRefreshFailureNotice({ background }) {
  return background ? "Today could not be refreshed. Reload to see the latest." : "Saved, but Today could not be refreshed. Reload to see the latest.";
}
